/* eslint-disable react-native/no-inline-styles */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Col, FontFamilies } from '../components'
import colors from '../components/colors'
import { floorsManualSort as floorsByOrder } from '../state/filter/consts'
import { TimetableEntry } from '../state/timetable/types'
import { NoEntriesMessage } from '../TimetableList/Entries'

const daysOfWeek = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
const displayDayOrder = ['Mi', 'Do', 'Fr', 'Sa', 'So', 'Mo', 'Di']

const firstColumnWidth = 90

interface TimetableEntryWithFavorite extends TimetableEntry {
  isFavorite?: boolean
}

function splitFloor(floor: string) {
  const dict = {
    Schuhkarton: 'Schuh karton',
    Kinderspace: 'Kinder Space',
    Extravaganza: 'Extraa Vaganza',
    Sonnendeck: 'Sonnendeck',
    Räuberhöhle: 'Räuber höhle',
    Trancefloor: 'Trance Floor',
    Luftschloss: 'Luft schloss',
    'Workshop Hangar_Parkett': 'Workshop Hangar Parkett',
    'Workshop Hangar_Domes': 'Workshop Hangar Domes',
    'Workshop Hangar_Tables': 'Workshop Hangar Tables',
    'Workshop Hangar_Yard': 'Workshop Hangar Yard',
    'La Ballena (Grand Palais)': 'La Ballena',
  }

  return dict[floor] || floor
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
`

const GridContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
`

const Grid = styled.div`
  position: relative;
  display: grid;
  min-width: fit-content;
  margin-bottom: 6rem;
`

const StickyCorner = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  z-index: ${colors.zIndex.timetableFlat.stickyCorner};
  background: ${colors.Flat.timeHeader.backgroundAlt};
  border-right: 1px solid ${colors.Flat.timeHeader.border};
  border-bottom: 1px solid ${colors.Flat.timeHeader.border};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-family: ${FontFamilies.RegularBold};
  font-size: 14px;
  padding: 8px 4px;
  width: ${firstColumnWidth}px;
  text-transform: uppercase;
  box-sizing: border-box;
`

const StickyShadowContainer = styled.div`
  position: sticky;
  height: 0;
  overflow: visible;
  z-index: 100;
  left: 0;
`

const StickyShadow = styled.div`
  width: ${firstColumnWidth}px;
  height: 1100px;
  box-shadow: 11px -4px 11px 0 rgba(0, 0, 0, 0.5);
`

const StickyTimeHeader = styled.div<{
  isCurrent?: boolean
  isEven?: boolean
  isFirst?: boolean
}>`
  position: sticky;
  box-sizing: border-box;
  top: 0;
  z-index: ${colors.zIndex.timetableFlat.timeHeader};
  background: ${(props) =>
    props.isCurrent
      ? colors.Flat.timeHeader.active.bg
      : props.isEven
      ? colors.Flat.timeHeader.background
      : colors.Flat.timeHeader.backgroundAlt};
  border-bottom: ${(props) =>
    `1px solid ${
      props.isCurrent ? colors.Flat.currentTime : colors.Flat.timeHeader.border
    }`};
  border-left: ${(props) =>
    props.isFirst
      ? '1px solid transparent'
      : `1px solid ${colors.Flat.timeHeader.border}`};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${(props) =>
    props.isCurrent ? colors.Flat.timeHeader.active.text : 'white'};
  padding: 6px 2px;
  min-width: 100px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
`

const StickyFloorHeader = styled.div<{ isEven: boolean; $isHidden?: boolean }>`
  position: sticky;
  left: 0;
  z-index: ${colors.zIndex.timetableFlat.timeHeader};
  background: ${(props) =>
    props.isEven
      ? colors.Flat.timeHeader.background
      : colors.Flat.timeHeader.backgroundAlt};
  border-right: 1px solid ${colors.Flat.timeHeader.border};
  border-bottom: 1px solid ${colors.Flat.timeHeader.border};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 9px 8px;
  color: white;
  font-weight: bold;
  width: ${firstColumnWidth}px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 10px;
  box-sizing: border-box;
  transform: translateX(${(props) => (props.$isHidden ? '-80px' : '0')});
  transition: transform 0.2s ease-in-out;
  text-transform: uppercase;
`

const GridCell = styled.div<{
  hasEvent?: boolean
  isEven?: boolean
  isCurrentTimeColumn?: boolean
}>`
  min-height: 50px;
  position: relative;
`

const EventBlock = styled.div<{
  duration: number
  genre: string
  isFavorite?: boolean
  isEven?: boolean
}>`
  position: relative;
  top: 2px;
  left: 2px;
  right: 2px;
  bottom: 2px;
  background: ${(props) => {
    if (props.isFavorite) return colors.Flat.active.background

    const genreColor: { even: string; odd: string } =
      colors.Flat.genres[props.genre] || colors.Flat.genres.default

    return props.isEven ? genreColor.even : genreColor.odd
  }};
  color: ${(props) =>
    props.isFavorite ? colors.Entry.favorite.text : 'white'};
  border-radius: 12px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  /* justify-content: flex-start; */
  justify-content: center;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    opacity: 0.8;
  }
`

const EventTitle = styled.div`
  font-size: 12px;
  font-family: ${FontFamilies.RegularBold};
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Number of lines to show */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis; /* Optional, for extra safety */
`

const CurrentTimeLine = styled.div<{ $left: number; $visible: boolean }>`
  position: absolute;
  top: 50px; /* Start after the header */
  bottom: 0;
  left: ${(props) => props.$left}px;
  width: 2px;

  background-color: ${colors.Flat.currentTime};
  z-index: ${colors.zIndex.timetableFlat.currentTimeLine};
  opacity: ${(props) => (props.$visible ? 0.9 : 0)};
  pointer-events: none;
`

type Props = {
  entries: TimetableEntryWithFavorite[]
  onEventClick?: (showId: string) => void
}

const TimetableFlat: React.FC<Props> = ({ entries, onEventClick }) => {
  const gridContainerRef = useRef<HTMLDivElement>(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [hasScrolledToCurrentTime, setHasScrolledToCurrentTime] =
    useState(false)
  // const [isFloorHideEnabled, setIsFloorHideEnabled] = useState(false)
  const [visibleDay, setVisibleDay] = useState<string>('')
  // const [isFloorHeaderHidden, setIsFloorHeaderHidden] = useState(false)
  // const lastScrollLeft = useRef(0)
  const timeHeaderRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // 60000ms = 1 minute

    return () => clearInterval(interval)
  }, [])

  // Add scroll event listener to track scroll direction
  // useEffect(() => {
  //   if (!isFloorHideEnabled) return

  //   const handleScroll = () => {
  //     if (!gridContainerRef.current) return

  //     const currentScrollLeft = gridContainerRef.current.scrollLeft
  //     const isScrollingRight = currentScrollLeft > lastScrollLeft.current

  //     setIsFloorHeaderHidden(isScrollingRight)
  //     lastScrollLeft.current = currentScrollLeft
  //   }

  //   const container = gridContainerRef.current
  //   if (container) {
  //     container.addEventListener('scroll', handleScroll)
  //   }

  //   return () => {
  //     if (container) {
  //       container.removeEventListener('scroll', handleScroll)
  //     }
  //   }
  // }, [isFloorHideEnabled])

  const {
    timeSlots,
    headerTimeSlots,
    floors,
    gridData,
    eventSpans,
    currentTimeSlotIndex,
    currentTimePosition,
  } = useMemo(() => {
    // Sort entries by start date first
    const sortedEntries = [...entries].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
    )

    // Get all unique time slots (30-minute intervals for grid)
    const allTimes = new Set<string>()
    // Get all unique hour slots for headers
    // const allHourTimes = new Set<string>()
    const allFloors = new Set<string>()

    // First, collect all possible hours to ensure complete hour slots
    const allHours = new Set<string>()

    if (sortedEntries.length > 0) {
      // Get first start hour
      const firstStart = new Date(sortedEntries[0].start)
      firstStart.setMinutes(0, 0, 0) // Start at the beginning of the hour

      // Get last end hour by finding the latest end time across all entries
      let latestEnd = new Date(sortedEntries[0].end)
      sortedEntries.forEach((entry) => {
        const entryEnd = new Date(entry.end)
        if (entryEnd > latestEnd) {
          latestEnd = entryEnd
        }
      })
      latestEnd.setMinutes(0, 0, 0) // Start at the beginning of the hour

      // Add all hours from first to last
      const current = new Date(firstStart)
      while (current <= latestEnd) {
        const day = daysOfWeek[current.getDay()]
        const hour = current.getHours().toString().padStart(2, '0')
        allHours.add(`${day} ${hour}:00`)
        current.setHours(current.getHours() + 1)
      }

      // Add one more hour
      // const nextHour = new Date(latestEnd)
      // nextHour.setHours(nextHour.getHours() + 1)
      // const day = daysOfWeek[nextHour.getDay()]
      // const hour = nextHour.getHours().toString().padStart(2, '0')
      // allHours.add(`${day} ${hour}:00`)
    }

    // Now generate 30-minute slots based on the complete hours
    Array.from(allHours).forEach((hourSlot) => {
      const [day, time] = hourSlot.split(' ')
      const hour = parseInt(time.split(':')[0])

      // Add both :00 and :30 slots for each hour
      allTimes.add(`${day} ${hour.toString().padStart(2, '0')}:00`)
      allTimes.add(`${day} ${hour.toString().padStart(2, '0')}:30`)
    })

    sortedEntries.forEach((entry) => {
      allFloors.add(entry.floor)
    })

    const sortedTimes = Array.from(allTimes).sort((a, b) => {
      const [dayA, timeA] = a.split(' ')
      const [dayB, timeB] = b.split(' ')

      const dayIndexA = displayDayOrder.indexOf(dayA)
      const dayIndexB = displayDayOrder.indexOf(dayB)
      if (dayIndexA !== dayIndexB) {
        return dayIndexA - dayIndexB
      }
      return timeA.localeCompare(timeB)
    })

    const sortedHourTimes = Array.from(allHours).sort((a, b) => {
      const [dayA, timeA] = a.split(' ')
      const [dayB, timeB] = b.split(' ')

      const dayIndexA = displayDayOrder.indexOf(dayA)
      const dayIndexB = displayDayOrder.indexOf(dayB)
      if (dayIndexA !== dayIndexB) {
        return dayIndexA - dayIndexB
      }
      return timeA.localeCompare(timeB)
    })

    const sortedFloors = Array.from(allFloors).sort((f1, f2) => {
      let pos1 = floorsByOrder.indexOf(f1)
      let pos2 = floorsByOrder.indexOf(f2)

      if (pos1 === -1) pos1 = 1000
      if (pos2 === -1) pos2 = 1000

      // console.log({ floorsByOrder, f1, pos1, f2, pos2 })

      return pos1 - pos2
    })

    // Calculate current time slot and precise position
    const now = currentTime
    const currentDay = daysOfWeek[now.getDay()]
    const currentHour = now.getHours().toString().padStart(2, '0')
    const currentMinute = now.getMinutes()

    // Round to nearest 30-minute slot for grid highlighting
    const roundedMinute = currentMinute < 30 ? 0 : 30
    const currentTimeSlot = `${currentDay} ${currentHour}:${roundedMinute
      .toString()
      .padStart(2, '0')}`

    // Find the index of current time slot
    const currentSlotIndex = sortedTimes.indexOf(currentTimeSlot)

    // Calculate precise line position within the slot
    const minuteOffset = currentMinute - roundedMinute
    const slotWidth = 50 // Each time slot is 50px wide
    const pixelOffset = (minuteOffset / 30) * slotWidth

    // Position: 90px for floor column + (slot index * 50px) + pixel offset within slot
    const currentTimeLeft =
      currentSlotIndex >= 0
        ? firstColumnWidth + currentSlotIndex * slotWidth + pixelOffset
        : -1

    const currentTimeInfo = {
      left: currentTimeLeft,
      visible: currentSlotIndex >= 0,
      timeString: `${currentHour}:${currentMinute.toString().padStart(2, '0')}`,
    }

    // Create grid data structure
    const grid: {
      [key: string]: { [key: string]: TimetableEntryWithFavorite[] }
    } = {}

    // Track event spans for proper grid positioning
    const spans: {
      [key: string]: {
        entry: TimetableEntryWithFavorite
        startIndex: number
        duration: number
      }[]
    } = {}

    // Initialize grid
    sortedFloors.forEach((floor) => {
      grid[floor] = {}
      spans[floor] = []
      sortedTimes.forEach((timeSlot) => {
        grid[floor][timeSlot] = []
      })
    })

    // Place events in grid and calculate spans
    sortedEntries.forEach((entry) => {
      const start = new Date(entry.start)
      const end = new Date(entry.end)
      const durationMinutes = (end.getTime() - start.getTime()) / (1000 * 60)
      const duration = Math.max(1, Math.ceil(durationMinutes / 30))

      // Find the first time slot that this event should occupy
      const startMinutes = start.getMinutes()
      const roundedStart = new Date(start)
      roundedStart.setMinutes(startMinutes < 30 ? 0 : 30, 0, 0)

      const day = daysOfWeek[roundedStart.getDay()]
      const hour = roundedStart.getHours().toString().padStart(2, '0')
      const minute = roundedStart.getMinutes().toString().padStart(2, '0')
      const firstTimeSlot = `${day} ${hour}:${minute}`

      const startIndex = sortedTimes.indexOf(firstTimeSlot)

      if (
        grid[entry.floor] &&
        grid[entry.floor][firstTimeSlot] &&
        startIndex >= 0
      ) {
        grid[entry.floor][firstTimeSlot].push(entry)
        spans[entry.floor].push({ entry, startIndex, duration })
      }
    })

    return {
      timeSlots: sortedTimes,
      headerTimeSlots: sortedHourTimes,
      floors: sortedFloors,
      gridData: grid,
      eventSpans: spans,
      currentTimeSlotIndex: currentSlotIndex,
      currentTimePosition: currentTimeInfo,
    }
  }, [entries, currentTime])

  // Auto-scroll to current time on mount
  useEffect(() => {
    if (
      !hasScrolledToCurrentTime &&
      currentTimeSlotIndex >= 0 &&
      gridContainerRef.current
    ) {
      const container = gridContainerRef.current
      const slotWidth = 50
      const scrollLeft = Math.max(
        0,
        currentTimeSlotIndex * slotWidth +
          firstColumnWidth -
          container.clientWidth / 2,
      )

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      })

      setHasScrolledToCurrentTime(true)

      // setTimeout(() => {
      //   setIsFloorHideEnabled(true)
      // }, 1000)
    }
  }, [currentTimeSlotIndex, hasScrolledToCurrentTime])

  // Set up intersection observer for time headers
  useEffect(() => {
    if (!gridContainerRef.current) return

    const observer = new IntersectionObserver(
      // eslint-disable-next-line @typescript-eslint/no-shadow
      (entries) => {
        // Find the last visible time header
        const visibleHeaders = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => entry.target as HTMLDivElement)
          .sort((a, b) => {
            const aRect = a.getBoundingClientRect()
            const bRect = b.getBoundingClientRect()
            return aRect.left - bRect.left
          })

        if (visibleHeaders.length > 0) {
          const lastVisibleHeader = visibleHeaders[visibleHeaders.length - 1]
          const timeSlot = lastVisibleHeader.getAttribute('data-timeslot')
          if (timeSlot) {
            const day = timeSlot.split(' ')[0]
            setVisibleDay(day)
          }
        }
      },
      {
        root: gridContainerRef.current,
        threshold: 0.5, // Header is considered visible when 50% is in view
      },
    )

    // Observe all time headers
    Object.values(timeHeaderRefs.current).forEach((header) => {
      if (header) observer.observe(header)
    })

    return () => observer.disconnect()
  }, [timeSlots]) // Re-run when time slots change

  // console.log('floors: ', floors)

  if (timeSlots.length === 0) {
    return (
      <Container>
        <Col mt={4} mb={4} ml={4} mr={4}>
          <NoEntriesMessage isFavoritesList={false} isOrderByName={false} />
        </Col>
      </Container>
    )
  }

  return (
    <Container>
      <GridContainer ref={gridContainerRef}>
        <StickyShadowContainer>
          <StickyShadow style={{ height: `${(floors.length + 1) * 50}px` }} />
        </StickyShadowContainer>
        <Grid
          style={{
            gridTemplateColumns: `${firstColumnWidth}px repeat(${timeSlots.length}, 50px)`,
            gridTemplateRows: `50px repeat(${floors.length}, 50px)`,
          }}
        >
          {/* Sticky corner cell */}
          <StickyCorner>{visibleDay || 'Floor / Time'}</StickyCorner>

          {/* Current time line */}
          <CurrentTimeLine
            $left={currentTimePosition.left}
            $visible={currentTimePosition.visible}
          />

          {/* Time headers */}
          {headerTimeSlots.map((timeSlot, index) => {
            const [day, time] = timeSlot.split(' ')
            const hour = time.split(':')[0]
            const isCurrentHour =
              currentTime.getHours().toString().padStart(2, '0') === hour &&
              daysOfWeek[currentTime.getDay()] === day

            const isEven = daysOfWeek.indexOf(day) % 2 === 0

            return (
              <StickyTimeHeader
                key={timeSlot}
                isCurrent={isCurrentHour}
                isEven={isEven}
                isFirst={index === 0}
                ref={(el) => (timeHeaderRefs.current[timeSlot] = el)}
                data-timeslot={timeSlot}
                style={{
                  gridColumn: `${index * 2 + 2} / span 2`,
                  minWidth: '100px',
                }}
              >
                <div
                  style={{
                    fontSize: '14px',
                    fontFamily: FontFamilies.RegularBold,
                    textTransform: 'uppercase',
                  }}
                >
                  {hour}:00
                </div>
              </StickyTimeHeader>
            )
          })}

          {/* Floor headers and cells */}
          {floors.map((floor, floorIndex) => (
            <React.Fragment key={floor}>
              <StickyFloorHeader
                isEven={floorIndex % 2 === 0}
                // $isHidden={isFloorHeaderHidden}
              >
                {splitFloor(floor)}
              </StickyFloorHeader>

              {timeSlots.map((timeSlot, timeIndex) => {
                const cellEvents = gridData[floor][timeSlot] || []
                const mainEvent = cellEvents[0]
                const eventSpan = eventSpans[floor]?.find(
                  (span) => span.entry.showId === mainEvent?.showId,
                )

                // Only render the event in the first cell of its span to avoid duplicates
                const shouldRenderEvent =
                  mainEvent && eventSpan && timeIndex === eventSpan.startIndex

                return (
                  <GridCell
                    key={`${floor}-${timeSlot}`}
                    hasEvent={!!shouldRenderEvent}
                    isEven={floorIndex % 2 === 0}
                    isCurrentTimeColumn={timeIndex === currentTimeSlotIndex}
                  >
                    {shouldRenderEvent && (
                      <EventBlock
                        duration={eventSpan.duration}
                        genre={mainEvent.genre}
                        isEven={floorIndex % 2 === 0}
                        isFavorite={mainEvent.isFavorite}
                        onClick={() => onEventClick?.(mainEvent.showId)}
                        // eslint-disable-next-line react-native/no-inline-styles
                        style={{
                          position: 'absolute',
                          top: '2px',
                          left: '2px',
                          right: `calc(-${
                            (eventSpan.duration - 1) * 50
                          }px + 2px)`,
                          bottom: '2px',
                          zIndex: 5,
                          // backgroundColor: 'blue',
                        }}
                      >
                        <EventTitle>
                          {mainEvent.name || mainEvent.artist}
                        </EventTitle>
                      </EventBlock>
                    )}
                  </GridCell>
                )
              })}
            </React.Fragment>
          ))}
        </Grid>
      </GridContainer>
    </Container>
  )
}

export default TimetableFlat
